import React, { useState, useEffect } from 'react';
import { Modal, Button, Dropdown, Form } from 'react-bootstrap';
import { createUserSalary, repeatLocalPaymentFromPreviousMonth } from '../../../../../../../../crud/management_api/user_salaries';
import { toast } from 'react-toastify';
import { X } from 'react-bootstrap-icons';
import './CreateCurrentSalary.scss';
import { FaMoneyBillWave,FaMoneyBills } from 'react-icons/fa6';
import { UserSalary } from '../../../../types';
import { use } from 'echarts';


interface CreateCurrentSalaryProps {
  show: boolean;
  handleClose: () => void;
  setData: React.Dispatch<React.SetStateAction<any[]>>;
  userId: number;
  data?:  UserSalary[]; // Datos opcionales para verificar si hay salarios previos
}

const CreateCurrentSalary: React.FC<CreateCurrentSalaryProps> = ({ show, handleClose, setData, userId, data }) => {
  const [localId, setLocalId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [year, setYear] = useState<number>(new Date().getFullYear() % 100); // Últimos dos dígitos del año
  const [totalPayed, setTotalPayed] = useState<number | null>(null);
  const [netPayed, setNetPayed] = useState<number | null>(null);
  const [hasPreviousSalary, setHasPreviousSalary] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    setLocalId(user?.local_id || null);
  }, []);

  useEffect(() => {
    if (data && userId) {
      const tieneSalario = data.some((s) => s.user_id === userId);
      setHasPreviousSalary(tieneSalario);
    } else {
      setHasPreviousSalary(false);
    }
  }, [data, userId]);

  const handleCloseModal = () => {
    setTotalPayed(null);
    setNetPayed(null);
    setMonth(new Date().getMonth() + 1);
    setYear(new Date().getFullYear() % 100);
    setShowForm(false);
    handleClose();
  };

  const handleCreate = async () => {
    if (localId && userId && month && year && totalPayed) {
      const monthYear = `${month.toString().padStart(2, '0')}/${year.toString().padStart(2, '0')}`;

      const newSalary = {
        local_id: localId,
        user_payment_number: null, // Generado por el backend
        month_year: monthYear,
        user_id: userId,
        total_payed: totalPayed,
        net_payed: netPayed,
        salary_metadata: null, // Metadatos deben ser null
      };

      // Buscar el username del usuario seleccionado
      let username = '';
      if (data && Array.isArray(data)) {
        // Buscar el username en los salarios existentes
        const found = data.find((s) => s.user_id === userId && s.username);
        if (found) {
          username = found.username;
        }
      }
      // Si no se encuentra, dejarlo vacío o como 'Desconocido'

      try {
        await createUserSalary(newSalary);
        toast.success('Salario creado con éxito');
        setData((prevData) => [
          ...prevData,
          { ...newSalary, username },
        ]);
        handleCloseModal();
      } catch (error) {
        console.error('Error al crear el salario:', error);
        toast.error('Error al crear el salario');
      }
    } else {
      toast.error('Por favor completa todos los campos obligatorios.');
    }
  };

  const handleRepeatPayment = async () => {
    if (localId && userId && data) {
      // Buscar el último salario del usuario
      const salariosUsuario = data.filter((s) => s.user_id === userId && s.month_year);
      if (salariosUsuario.length === 0) {
        toast.error('No hay salario anterior para repetir.');
        return;
      }
      // Ordenar por mes/año para encontrar el más reciente
      const ultimoSalario = salariosUsuario.sort((a, b) => {
        if (!a.month_year || !b.month_year) return 0;
        const [mesA, anioA] = a.month_year.split('/').map(Number);
        const [mesB, anioB] = b.month_year.split('/').map(Number);
        return anioB !== anioA ? anioB - anioA : mesB - mesA;
      })[0];
      if (!ultimoSalario.month_year) {
        toast.error('No se pudo determinar el mes/año del último salario.');
        return;
      }
      // Calcular el siguiente mes/año
      let mes = Number(ultimoSalario.month_year.split('/')[0]);
      let anio = Number(ultimoSalario.month_year.split('/')[1]);
      mes = mes === 12 ? 1 : mes + 1;
      anio = mes === 1 ? anio + 1 : anio;
      const monthYear = `${mes.toString().padStart(2, '0')}/${anio.toString().padStart(2, '0')}`;
      const payload = {
        local_id: localId,
        month_year: monthYear,
        user_id: userId,
      };
      // Buscar el username del usuario seleccionado
      let username = '';
      if (data && Array.isArray(data)) {
        const found = data.find((s) => s.user_id === userId && s.username);
        if (found) {
          username = found.username;
        }
      }
      try {
        const response = await repeatLocalPaymentFromPreviousMonth(payload);
        const newData = response.data;
        toast.success('Pago repetido con éxito');
        setData((prevData) => [
          ...prevData,
          { ...newData, username },
        ]);
        handleCloseModal();
      } catch (error) {
        console.error('Error al repetir el pago:', error);
        toast.error('Error al repetir el pago');
      }
    } else {
      toast.error('Por favor completa todos los campos obligatorios.');
    }
  };
  
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

  if (!showForm) {
    return (
      <Modal show={show} onHide={handleCloseModal} centered className='create-salary-modal'>
        <Modal.Header>
          <div className="create-salary-modal-title-container">
            <Modal.Title className="create-salary-modal-title">Opciones de Salario</Modal.Title>
          </div>
          <Button variant="none" onClick={handleCloseModal}>
            <X size={24} />
          </Button>
        </Modal.Header>
        <Modal.Body>
          <div className='create-salary-modal-type-container-selector'>
            <div className='create-salary-modal-type-section' onClick={() => setShowForm(true)}>
              <span className='create-salary-modal-type-icon'>
                <FaMoneyBillWave/>
              </span>
              <div className='create-salary-modal-type-info'>
                <span className='create-salary-modal-type-title'>Ingresar nuevo monto</span>
                <span className='create-salary-modal-type-description'>Crear un nuevo salario para el empleado</span>
              </div>
            </div>
            {hasPreviousSalary && (
              <div className='create-salary-modal-type-section' onClick={handleRepeatPayment}>
                <span className='create-salary-modal-type-icon'>
                  <FaMoneyBills/>
                </span>
                <div className='create-salary-modal-type-info'>
                  <span className='create-salary-modal-type-title'>Repetir mes anterior</span>
                  <span className='create-salary-modal-type-description'>Repite el pago del mes actual al próximo</span>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={handleCloseModal} centered className='create-salary-modal'>
      <Modal.Header>
        <div className="create-salary-modal-title-container">
          <Modal.Title className="create-salary-modal-title">Crear salario actual</Modal.Title>
        </div>
        <Button variant="none" onClick={handleCloseModal}>
          <X size={24} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mp-formgroup horizontal">
            <Form.Label>Mes</Form.Label>
            <Dropdown className="mp-dropdown" onSelect={(eventKey) => setMonth(Number(eventKey))}>
              <Dropdown.Toggle variant="none" id="dropdown-month">
                {monthNames[month - 1]}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {monthNames.map((name, index) => (
                  <Dropdown.Item key={index + 1} eventKey={(index + 1).toString()}>
                    {name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          <Form.Group className="mp-formgroup horizontal">
            <Form.Label>Año</Form.Label>
            <Dropdown className="mp-dropdown" onSelect={(eventKey) => setYear(Number(eventKey))}>
              <Dropdown.Toggle variant="none" id="dropdown-year">
                {years.find((y) => y % 100 === year) || years[0]}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {years.map((y) => (
                  <Dropdown.Item key={y} eventKey={(y % 100).toString()}>
                    {y}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

                    <Form.Group className="mp-formgroup horizontal">
            <Form.Label>Total desembolsado</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              placeholder="Ingrese el total pagado"
              value={totalPayed !== null ? totalPayed : ''}
              onChange={e => {
                const val = e.target.value;
                if (val === '' || parseFloat(val) >= 0) {
                  setTotalPayed(Number(val));
                }
              }}
              onKeyDown={e => {
                if (e.key === '-' || e.key.toLowerCase() === 'e') {
                  e.preventDefault();
                }
              }}
            />
          </Form.Group>

                    <Form.Group className="mp-formgroup horizontal">
            <Form.Label>Recibido por el empleado</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              placeholder="Ingrese el pago neto"
              value={netPayed !== null ? netPayed : ''}
              onChange={e => {
                const val = e.target.value;
                if (val === '' || parseFloat(val) >= 0) {
                  setNetPayed(Number(val));
                }
              }}
              onKeyDown={e => {
                if (e.key === '-' || e.key.toLowerCase() === 'e') {
                  e.preventDefault();
                }
              }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="none" className="form-button-cancel" onClick={handleCloseModal}>
          Cerrar
        </Button>
        <Button variant="none" className="form-button-create" onClick={handleCreate}>
          Crear
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateCurrentSalary;
